import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import i18next from 'i18next';
import { observer } from 'mobx-react';
import Alert from 'react-s-alert';
import styled from 'styled-components';
import { ComponentRegistry } from 'core/registry';
import { clientStore } from 'apps/client/app';
import { Link, routerStore } from 'core/routing';

import { SvgIcon, Heading, Tooltip, Button, Box, Avatar } from 'components';
import { Close, Clock } from 'icons';
import Caret from 'components/Select/caret';

import { FormField } from 'libs/forms/styles';
import { Dropdown } from 'apps/autocomplete/views/dropdown';

import {
  Select,
  Alert as ChakraAlert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import { modalStore } from 'apps/modals/app';
import { ConfirmAction } from 'apps/modals/confirmAction/index';

import {
  UserSettingsWrapper,
  SearchWrapper,
  ParticipantsWrapper,
  NativeSelectWrapper,
  NativeSelect,
  CaretWrapper,
} from 'libs/rendering/components/header/collaboratorsSettings.styled';
import { emailValidator } from 'libs/forms/validators';

import { SectionTitle } from 'apps/group/styles';

import {
  canUpdateChooser,
  getPermissionValueMap,
  getUsernameByEmail,
} from 'utils';

const StyledDropdown = styled(Dropdown)`
  width: 100%;
  background-color: red;
`;

const prettifiedGroupName = {
  owner: 'Owner',
  admin: 'Admin',
  collaborator: 'Editor',
  'limited-collaborator': 'Viewer',
  member: 'Editor',
  viewer: 'Viewer',
};

const permissionTooltip = {
  admin: '(View, Comment, Download, Upload Revisions and Manage Collaborators)',
  collaborator: '(View, Comment, Download and Upload Revisions)',
  'limited-collaborator': '(View and Comment)',
  viewer: '(View and Comment)',
};

const intrinsicPermissionToolTip = 'inherited from team members permission';

// These are the old group names
const initiativeGroupNames = ['owner', 'member', 'admin', 'viewer'];
const projectGroupNames = ['limited-collaborator', 'collaborator', 'admin'];
const freeGroupNames = ['limited-collaborator', 'viewer'];

const isMemberOfGroup = (group, username) => {
  const member = group.members.find(m => m.profile.username === username);
  if (member) return true;
  return false;
};

const LimitedInvites = () => {
  return (
    <Box style={{ marginTop: '2em' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          rowGap: '1em',
          alignItems: 'center',
        }}
      >
        <Link name='home.pricing'>Learn more about our Plans</Link>
      </div>
    </Box>
  );
};

const ParticipantsComponent = observer(props => {
  const {
    participants,
    canManipulateRoles,
    whichGroups,
    groupsDict,
    handleSelectChange,
    creator,
    availableSeats,
    isProject,
    collaborators,
  } = props;

  const sortedParticipants = participants.sort((a, b) => {
    const groupValues = getPermissionValueMap(isProject);

    const groupA = a.group.name;
    const groupB = b.group.name;

    return groupValues[groupB] - groupValues[groupA];
  });

  const sortedGroups = whichGroups.sort((a, b) => {
    const groupValues = getPermissionValueMap(isProject);

    return groupValues[b] - groupValues[a];
  });

  const currentUser = sortedParticipants.find(
    ({ profile }) => profile?.id === clientStore.profileResponse?.id,
  );

  const permissionValueMap = getPermissionValueMap(isProject);

  const createUser =
    creator &&
    sortedParticipants.find(
      ({ profile }) => profile?.id === creator.profile?.id,
    );

  const participantList = sortedParticipants.map(participant => {
    const { profile, invite, displayName, groupMember } = participant;
    let optionList = null;

    const canUpdateRole = canUpdateChooser(currentUser, participant, isProject);

    let showSelect = canUpdateRole;

    let currentGroupname = currentUser?.group?.name;

    let limitRoleSelection = false;

    const isEmail = emailValidator(displayName);

    if (isProject && !isEmail) {
      if (groupMember) {
        limitRoleSelection = !groupMember.intrinsic;
      }
    }

    if (createUser) {
      showSelect = canUpdateChooser(createUser, participant, isProject);
      currentGroupname = createUser.group?.name;
    }

    if (routerStore.route.params.initiative && limitRoleSelection) {
      showSelect = true;
    }

    if (canManipulateRoles) {
      // Create an option for each group as well as the delete one
      optionList = (
        <>
          {sortedGroups
            .filter(groupName => {
              const showCurrentGroup =
                currentGroupname &&
                permissionValueMap[groupName] <=
                  permissionValueMap[currentGroupname];

              const canAdjustToAnyRole = !freeGroupNames.includes(
                participant?.group?.name,
              );

              const adjustable = canAdjustToAnyRole
                ? true
                : permissionValueMap[groupName] <=
                  permissionValueMap[participant?.group?.name];

              const isOutsideCollaborator = collaborators.some(
                c =>
                  c.username === participant?.profile?.username ||
                  participant?.username,
              );

              return availableSeats
                ? showCurrentGroup
                : showCurrentGroup && (isOutsideCollaborator || adjustable);
            })
            .filter(groupName => {
              // if it's project or shared file, hide the admin option
              //  a. when it's intrinsic, make the admin back, since it's disable, but we need the value to show the admin
              //  b. if it's not intrinsic, hide the admin
              if (
                isProject &&
                ['admin'].includes(groupName) &&
                !limitRoleSelection
              ) {
                return false;
              }

              return true;
            })
            .map(groupName => {
              const g = groupsDict[groupName];

              const optionTestId = `${displayName}-${prettifiedGroupName[
                g.name
              ].toLowerCase()}`;

              return (
                <option
                  data-testid={optionTestId}
                  value={g.name.toLowerCase()}
                  key={`g-${g.name}`}
                >
                  {prettifiedGroupName[g.name]}
                </option>
              );
            })}
          <option
            data-testid={`remove-${displayName}}`}
            value='delete'
            key='g-delete'
          >
            {!props.isProject ? `Remove Member` : 'Remove Collaborator'}
          </option>
        </>
      );
    }

    const avatar = (
      <Avatar
        name={profile?.imageFallbackChar || getUsernameByEmail(displayName)[0]}
        src={profile?.avatar?.url}
        size='medium'
        style={{ cursor: profile?.username ? 'pointer' : 'not-allowed' }}
      />
    );

    const avatarContent = profile?.username ? (
      <Link name='profile' params={{ profile: profile.username }}>
        {avatar}
      </Link>
    ) : (
      avatar
    );

    return (
      <Box
        data-testid={`collaborator-row-${profile?.username || displayName}`}
        key={profile?.username || displayName}
        sx={{
          alignItems: 'center',
          display: 'flex',
        }}
        mb={4}
      >
        {avatarContent}
        <Box
          ml={2}
          sx={{
            flexDirection: 'column',
            display: 'flex',
            width: 164,
          }}
          style={{ flex: 1 }}
        >
          <Heading size='xsmall'>{profile?.fullName || displayName}</Heading>
          {profile?.username && (
            <Heading size='xsmall' variant='minimal'>
              {profile?.username}
            </Heading>
          )}
        </Box>
        {invite && invite.status === 'PENDING' && (
          <Box
            ml={8}
            sx={{ display: 'inline-flex' }}
            data-tip='Pending confirmation'
            data-for='permissionsTooltip'
            style={{ marginLeft: '195px' }}
          >
            <SvgIcon icon={<Clock />} />
          </Box>
        )}

        {showSelect && (
          <NativeSelectWrapper style={{ marginLeft: 'auto' }}>
            <CaretWrapper>
              <Caret show={false} size='xsmall' />
            </CaretWrapper>
            <NativeSelect
              disabled={limitRoleSelection}
              value={participant.group.name}
              onChange={handleSelectChange(participant)}
              data-for='permissionsTooltip'
              data-testid={`moveUserToGroupRole-${displayName}`}
              data-tip={
                limitRoleSelection
                  ? intrinsicPermissionToolTip
                  : permissionTooltip[participant.group.name]
              }
              style={{ backgroundColor: 'transparent', fontWeight: 'normal' }}
            >
              {optionList}
            </NativeSelect>
          </NativeSelectWrapper>
        )}
        {!showSelect && (
          <p
            className='capitalize mx-4 text-xs'
            data-testid={`${participant.group.name.toLowerCase()}-${displayName}`}
          >
            {prettifiedGroupName[participant.group.name]}
          </p>
        )}
      </Box>
    );
  });

  return participantList;
});

const CollaboratorSettings = observer(props => {
  const { manager, closeMenu, isProject, borderless, getRepeatUsers } = props;

  let restrictedAccess = null;

  if (isProject && 'projectManager' in manager) {
    const { project } = manager.projectManager;
    restrictedAccess = project?.isRestricted;
  }

  const [targetGroup, setTargetGroup] = useState(
    isProject ? 'limited-collaborator' : 'member',
  );

  useEffect(() => {
    if (!manager.availableSeats) {
      setTargetGroup(isProject ? 'limited-collaborator' : 'viewer');
    }
  }, [manager.availableSeats]);

  useEffect(() => {
    if (!clientStore.profileResponse) {
      clientStore.WhoAmI();
    }
  }, [clientStore.profileResponse]);

  const groupsDict = {};
  let whichGroups = null;
  if (isProject) {
    whichGroups = projectGroupNames;
  } else {
    whichGroups = initiativeGroupNames;
  }

  if (manager.groups.length === 0) {
    return null;
  }

  // For each string in initiativeGroupNames, do something
  for (let i = 0; i < whichGroups.length; i++) {
    const groupName = whichGroups[i];
    const group = manager.groups.find(g => g.name === groupName);
    groupsDict[groupName] = group;
  }

  // const fieldStore =groupsDict.admin?.membersForm.fieldStores.get('newMembers');
  const fieldStore =
    groupsDict[targetGroup].membersForm.fieldStores.get('newMembers');

  const isInitiativeSettingsRoute =
    routerStore.route.name === 'workspace.spaceSettings.teams';

  if (isInitiativeSettingsRoute) {
    fieldStore.conf.placeholder = 'Type an Email.';
  }

  const ListField = ComponentRegistry.get('forms.ListField');
  const refs = {};

  const setInputRef = input => {
    refs.newMembers = input;
  };

  const { participants } = manager.groups;

  const creator = manager.sharedFile?.creator || undefined;

  const handleSelectChange = participant => event => {
    if (event.target.value === 'delete') {
      event.preventDefault();
      modalStore.SetModalComponent(ConfirmAction, {
        callBack: p => {
          if (p.invite && p.invite.status === 'PENDING') {
            p.group.RemoveInvite(p.invite.id);
          } else {
            p.group.RemoveMember(p.profile.username, () => {
              manager.groups.RemoveMemberFromApps(p.profile.username);
            });
          }
        },
        callBackArgs: participant,
        title: `Are you sure that you want to remove ${participant.displayName}?`,
        undo: true,
      });
    } else {
      manager.groups.MoveParticipant(participant, event.target.value);
    }
  };

  let canAdmin = false;

  if (isProject) {
    canAdmin = isMemberOfGroup(
      groupsDict.admin,
      clientStore.signedInProfile.username,
    );
  } else {
    canAdmin = isMemberOfGroup(
      groupsDict.owner,
      clientStore.signedInProfile.username,
    );
  }

  let canUpdate = false;
  if (isProject) {
    canUpdate = isMemberOfGroup(
      groupsDict.admin,
      clientStore.signedInProfile.username,
    );

    canUpdate =
      canUpdate ||
      isMemberOfGroup(
        groupsDict.collaborator,
        clientStore.signedInProfile.username,
      );
  } else {
    canUpdate = isMemberOfGroup(
      groupsDict.owner,
      clientStore.signedInProfile.username,
    );
    canUpdate =
      canUpdate ||
      isMemberOfGroup(groupsDict.admin, clientStore.signedInProfile.username);
  }

  const canManipulateRoles = canAdmin || canUpdate;

  const copyLinkComponent = canUpdate && (
    <Box style={{ border: 'none' }}>
      <Button
        text={<div style={{ fontWeight: '100' }}>Copy Invite link</div>}
        size='small'
        appearance='plain'
        variant='primary'
        data-testid='copy-invite-link-button'
        onClick={() => {
          const invite = manager.inviteLink
            ? manager.inviteLink
            : manager.invite;

          // Could this be better with a computed?
          invite.init();
          invite.expiresInDays = 30;

          invite.CreateLink(result => {
            invite.url = result?.createInviteLink?.inviteLink?.url;
            invite.CopyLinkToClipboard();
          });
        }}
      />
    </Box>
  );

  const participantList = (
    <ParticipantsComponent
      whichGroups={whichGroups}
      groupsDict={groupsDict}
      participants={participants}
      canManipulateRoles={canManipulateRoles}
      handleSelectChange={handleSelectChange}
      creator={creator}
      showRoleSelect={!restrictedAccess}
      isProject={isProject}
      collaborators={manager.collaborators}
      availableSeats={manager.availableSeats}
    />
  );

  let roleSelectorOptions = null;

  roleSelectorOptions = whichGroups
    .reverse()
    .filter(groupName => {
      // if it's project or shared file, hide the admin option
      if (isProject && ['admin'].includes(groupName)) {
        return false;
      }
      return true;
    })
    .map(groupName => {
      return (
        <option
          date-testid={`${prettifiedGroupName[
            groupName
          ].toLowerCase()}-role-option`}
          value={groupName}
          key={groupName}
        >
          {prettifiedGroupName[groupName]}
        </option>
      );
    });

  let outsideCollaboratorsNumber = 0;

  groupsDict[targetGroup].newMembers.apps.forEach(memberApp => {
    const { username } = memberApp;

    const collaborator = manager.collaborators.find(
      c => c.username === username,
    );

    if (collaborator) {
      outsideCollaboratorsNumber += 1;
    }
  });

  let inviteDisabled = true;

  const inviteNumber = groupsDict[targetGroup].newMembers.apps.length;

  inviteDisabled =
    inviteNumber - outsideCollaboratorsNumber > manager.availableSeats;

  if (targetGroup === 'limited-collaborator' || targetGroup === 'viewer') {
    inviteDisabled = false;
  }

  return (
    <>
      <Tooltip delayShow={300} place='right' id='permissionsTooltip' />
      <UserSettingsWrapper
        data-testid='project-settings-collaborators'
        borderless={borderless}
        style={{ maxWidth: '70%' }}
      >
        {canUpdate && (
          <div className={`flex justify-between ${borderless ? '' : 'mt-2'}`}>
            <SectionTitle
              level={2}
              size='medium'
              style={{ paddingBottom: '0.2em', marginLeft: 16 }}
            >
              {isProject ? 'Collaborators' : 'Members'}
            </SectionTitle>
            {/* Only show the close button if we're on the header case, i.e. we're showing the border */}
            {!borderless && (
              <SvgIcon
                className='cursor-pointer w-fit h-fit'
                icon={<Close />}
                size='medium'
                onClick={closeMenu}
              />
            )}
          </div>
        )}

        <ParticipantsWrapper>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {participantList}
          </Box>
        </ParticipantsWrapper>

        <>
          <Box
            style={{
              marginTop: '0',
              marginLeft: '1em',
              marginBottom: '1em',
              height: '1px',
              backgroundColor: '#d8d8d8',
            }}
          ></Box>

          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}>
            <Button
              data-testid='invite-text-button'
              text={<>Invite {props.isProject ? 'Collaborator' : 'Member'}s</>}
              size='small'
              appearance='plain'
              variant='tertiary'
              icon='user_plus'
              iconPosition='left'
              style={{
                maxWidth: '180px',
                marginLeft: '1.5em',
              }}
            />
            {!inviteDisabled && (
              <div
                data-testid='invite-copy-link-wrapper'
                className='flex justify-center items-center'
              >
                or &nbsp;
                {copyLinkComponent}
              </div>
            )}
          </Box>

          <>
            <SearchWrapper>
              <FormField>
                <ListField
                  ref={setInputRef}
                  iconName='search'
                  fieldStore={fieldStore}
                  noValidationIcon
                  responsive
                  inputAlwaysVisible={true}
                />
                {!isInitiativeSettingsRoute && (
                  <StyledDropdown
                    inputFields={refs}
                    fieldName='newMembers'
                    manager={fieldStore.optionsManager}
                    dataTestId='collaborator-user-dropdown'
                  />
                )}
              </FormField>
              <Box w='250px'>
                <Select
                  data-testid='project-role-select'
                  onChange={e => {
                    // Move the new members to the new target group
                    groupsDict[e.target.value].newMembers =
                      groupsDict[targetGroup].newMembers;
                    setTargetGroup(e.target.value);
                  }}
                  value={targetGroup}
                  style={{
                    backgroundColor: 'transparent',
                    fontWeight: 'normal',
                    padding: '8px, 48px, 8px, 16px',
                    border: '0px',
                    fontSize: '0.75rem',
                    lineHeight: '1.15',
                    width: '125px',
                  }}
                >
                  {roleSelectorOptions}
                  {/*  */}
                </Select>
              </Box>
            </SearchWrapper>
            {borderless && (
              <Box
                style={{
                  height: '2px',
                  backgroundColor: '#b8b8b8',
                  width: '97%',
                  marginLeft: '1em',
                  marginBottom: '1em',
                }}
              ></Box>
            )}
            <Button
              text='Send invite'
              disabled={inviteDisabled}
              data-testid='collabortors-send-invite'
              size='small'
              // loading={limitedCollaboratorGroup.loading.inviteMembers}
              onClick={() => {
                const invitedUsers = fieldStore.value.map(v => {
                  return v?.username;
                });

                const repeatUsers = getRepeatUsers(invitedUsers);

                if (repeatUsers.length) {
                  Alert.error(
                    i18next.t(
                      `${repeatUsers.join(', ')} already exists in the team`,
                    ),
                  );
                  return;
                }

                const groupValues = getPermissionValueMap(targetGroup);

                const removedUsernames = new Set();

                const pendingInvites = new Set();

                if (groupsDict[targetGroup].newMembers.apps.length === 0) {
                  Alert.success(
                    "It seems you forgot to enter the email or username. Don't forget to use TAB or Enter for adding it to the list",
                    5000,
                  );
                  return;
                }

                // Check if the user is already in any of the other groups with more privileges
                groupsDict[targetGroup].newMembers.apps.forEach(memberApp => {
                  const { username } = memberApp;

                  // Iterate through all the groups of the

                  manager.groups.apps.forEach(group => {
                    const groupName = group.name;

                    // Compare the member to the group members
                    group.members.apps.forEach(app => {
                      if (
                        app.profile.username === username &&
                        groupValues[targetGroup] <= groupValues[groupName]
                      ) {
                        groupsDict[targetGroup].newMembers.apps = groupsDict[
                          targetGroup
                        ].newMembers.apps.filter(
                          member => member.username !== username,
                        );
                        removedUsernames.add(username);
                      }
                    });
                    // Also check the pending invitations
                    group.pendingInvites.apps.forEach(app => {
                      if (
                        `@${app.username}` === username &&
                        groupValues[targetGroup] <= groupValues[groupName]
                      ) {
                        groupsDict[targetGroup].newMembers.apps = groupsDict[
                          targetGroup
                        ].newMembers.apps.filter(
                          member => member.username !== username,
                        );
                        pendingInvites.add(username);
                        removedUsernames.add(username);
                      }
                    });
                  });
                });

                // Preapre the message to be shown with the ignored invitations (if any)
                if (removedUsernames.size !== 0) {
                  let message = '';

                  if (removedUsernames.size === 1) {
                    const [username] = removedUsernames;
                    message = `${username} is already a collaborator`;
                  } else {
                    Array.from(removedUsernames).forEach((username, index) => {
                      if (index === 0) {
                        message = `${username} `;
                      } else if (index === removedUsernames.size - 1) {
                        // last element
                        message += ` and ${username} are already collaborators`;
                      } else {
                        message += `, ${username}`;
                      }
                    });
                  }

                  Alert.success(message, 5000);
                }

                if (groupsDict[targetGroup].newMembers.apps.length !== 0) {
                  groupsDict[targetGroup].InviteMembers();
                }
              }}
              outline
              style={{ width: 'fit-content', margin: '0 auto' }}
              icon='arrow_right'
              iconPosition='right'
            />
            {inviteDisabled && (
              <div className='text-center mt-8'>
                <ChakraAlert status='info'>
                  <AlertIcon />
                  <AlertDescription className='flex justify-center items-center'>
                    <span>
                      In order to invite editor please
                      {!manager.isStarterPlan && 'Free up a seat, or '}
                      <span
                        data-testid={
                          manager.isStarterPlan
                            ? 'upgrade-now'
                            : 'buy-an-extra-seat'
                        }
                        className='cursor-pointer text-blue-500 inline-block ml-1'
                        onClick={() => {
                          let navigateTo = 'workspace.spaceSettings.billing';

                          if (manager.isStarterPlan) {
                            navigateTo = 'home.newInitiative';
                          }

                          routerStore.navigate(navigateTo, {
                            spacePath: props.spaceSlug,
                          });
                        }}
                      >
                        {manager.isStarterPlan
                          ? 'Create a Team'
                          : 'buy an extra seat'}
                      </span>
                      .
                    </span>
                  </AlertDescription>
                </ChakraAlert>
              </div>
            )}
          </>
        </>

        {restrictedAccess && <LimitedInvites />}
      </UserSettingsWrapper>
    </>
  );
});

CollaboratorSettings.propTypes = {
  manager: PropTypes.object,
  closeMenu: PropTypes.func,
  isProject: PropTypes.bool,
  spaceSlug: PropTypes.string,
};

export default CollaboratorSettings;
